import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { localizedPath, SUPPORTED_LANGS, PAGE_SLUGS } from '../utils/routes';

const SITE_URL = 'https://doubleastudio.ch';
const OG_IMAGE = 'https://customer-assets.emergentagent.com/job_editorialluxe/artifacts/a8bbi0ky_image.png';
const SITE_NAME = 'Double A Studio';

const PAGE_SEO = {
  home: {
    it: { title: 'Double A Studio | Web Design & Branding Digitale Svizzera', description: 'Agenzia di web design e branding digitale in Svizzera. Creiamo siti web professionali, app mobile, video AI, SEO e soluzioni digitali per PMI. Preventivo gratuito.' },
    en: { title: 'Double A Studio | Web Design & Digital Branding Switzerland', description: 'Web design and digital branding agency in Switzerland. Professional websites, mobile apps, AI video, SEO and digital solutions for SMBs. Free quote.' },
    de: { title: 'Double A Studio | Webdesign & Digital Branding Schweiz', description: 'Webdesign- und Digital-Branding-Agentur in der Schweiz. Professionelle Websites, Mobile Apps, AI-Video, SEO und digitale Lösungen für KMU. Kostenloses Angebot.' },
    fr: { title: 'Double A Studio | Web Design & Branding Digital Suisse', description: 'Agence de web design et branding digital en Suisse. Sites web professionnels, apps mobiles, vidéo AI, SEO et solutions digitales pour PME. Devis gratuit.' },
  },
  about: {
    it: { title: 'Chi Siamo | Double A Studio — Alexandar & Arianna', description: 'Scopri il team di Double A Studio: Alexandar (Web Developer) e Arianna (Brand Strategist). Agenzia digitale svizzera con passione per design, tecnologia e innovazione.' },
    en: { title: 'About Us | Double A Studio — Alexandar & Arianna', description: 'Meet the Double A Studio team: Alexandar (Web Developer) and Arianna (Brand Strategist). Swiss digital agency passionate about design, technology and innovation.' },
    de: { title: 'Über Uns | Double A Studio — Alexandar & Arianna', description: 'Lernen Sie das Double A Studio Team kennen: Alexandar (Webentwickler) und Arianna (Markenstrategie). Schweizer Digitalagentur für Design, Technologie und Innovation.' },
    fr: { title: 'À Propos | Double A Studio — Alexandar & Arianna', description: 'Découvrez l\'équipe Double A Studio: Alexandar (Développeur Web) et Arianna (Stratège de Marque). Agence digitale suisse passionnée par le design et l\'innovation.' },
  },
  portfolio: {
    it: { title: 'Portfolio | Double A Studio — I Nostri Progetti Web', description: 'Scopri i progetti realizzati da Double A Studio: siti web, web app e soluzioni digitali per ristoranti, marketplace e PMI svizzere.' },
    en: { title: 'Portfolio | Double A Studio — Our Web Projects', description: 'Discover projects by Double A Studio: websites, web apps and digital solutions for restaurants, marketplaces and Swiss SMBs.' },
    de: { title: 'Portfolio | Double A Studio — Unsere Webprojekte', description: 'Entdecken Sie die Projekte von Double A Studio: Websites, Web-Apps und digitale Lösungen für Restaurants, Marktplätze und Schweizer KMU.' },
    fr: { title: 'Portfolio | Double A Studio — Nos Projets Web', description: 'Découvrez les projets de Double A Studio: sites web, applications web et solutions digitales pour restaurants, marketplaces et PME suisses.' },
  },
  'ai-video': {
    it: { title: 'Video AI | Double A Studio — Produzione Video con Intelligenza Artificiale', description: 'Servizio di produzione video AI: spot pubblicitari, contenuti social, presentazioni aziendali e branding video. Qualità cinematografica a costi accessibili.' },
    en: { title: 'AI Video | Double A Studio — AI-Powered Video Production', description: 'AI video production service: advertising spots, social content, corporate presentations and branding videos. Cinematic quality at accessible costs.' },
    de: { title: 'AI-Video | Double A Studio — AI-gestützte Videoproduktion', description: 'AI-Videoproduktion: Werbespots, Social-Media-Inhalte, Unternehmenspräsentationen und Branding-Videos. Kinoqualität zu erschwinglichen Kosten.' },
    fr: { title: 'Vidéo AI | Double A Studio — Production Vidéo par Intelligence Artificielle', description: 'Production vidéo AI: spots publicitaires, contenus sociaux, présentations et vidéos de branding. Qualité cinématographique à coûts accessibles.' },
  },
};

const HREFLANG_MAP = {
  it: 'it-CH', en: 'en', de: 'de-CH', fr: 'fr-CH',
};

const LOCALE_MAP = {
  it: 'it_CH', en: 'en_US', de: 'de_CH', fr: 'fr_CH',
};

const WEBSITE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: 'Web design and digital branding agency in Switzerland. Professional websites, mobile apps, AI video production, SEO and digital solutions.',
  inLanguage: ['it-CH', 'de-CH', 'fr-CH', 'en'],
  potentialAction: {
    '@type': 'SearchAction',
    target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/it?q={search_term_string}` },
    'query-input': 'required name=search_term_string',
  },
};

const ORG_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/images/logo_transparent.png`,
  email: 'info@doubleastudio.ch',
  telephone: '+41 76 627 08 82',
  foundingDate: '2025',
  foundingLocation: { '@type': 'Country', name: 'Switzerland' },
  founder: [
    { '@type': 'Person', name: 'Alexandar Posinak', jobTitle: 'Co-Founder & Web Developer' },
    { '@type': 'Person', name: 'Arianna Adriani', jobTitle: 'Co-Founder & Brand Strategist' },
  ],
  sameAs: ['https://www.instagram.com/doubleastudioch'],
};

const SERVICE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: SITE_NAME,
  url: SITE_URL,
  logo: OG_IMAGE,
  image: OG_IMAGE,
  description: 'Agenzia di web design e branding digitale in Svizzera. Siti web, app mobile, video AI, SEO e soluzioni digitali.',
  telephone: '+41 76 627 08 82',
  email: 'info@doubleastudio.ch',
  address: { '@type': 'PostalAddress', addressLocality: 'Svizzera', addressRegion: 'Argovia', addressCountry: 'CH' },
  geo: { '@type': 'GeoCoordinates', latitude: '47.3887', longitude: '8.0456' },
  areaServed: [
    { '@type': 'Country', name: 'Svizzera' },
    { '@type': 'AdministrativeArea', name: 'Ticino' },
    { '@type': 'AdministrativeArea', name: 'Argovia' },
  ],
  serviceType: ['Web Design', 'Sviluppo Siti Web', 'Branding Digitale', 'App Mobile', 'SEO', 'Video AI', 'E-commerce', 'Soluzioni AI'],
  priceRange: '$$',
  openingHoursSpecification: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'], opens: '00:00', closes: '23:59' },
};

function buildBreadcrumbs(lang, page, projectTitle) {
  const items = [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/${lang}` }];

  if (page === 'about') {
    items.push({ '@type': 'ListItem', position: 2, name: PAGE_SEO.about[lang]?.title?.split('|')[0]?.trim() || 'About', item: `${SITE_URL}${localizedPath(lang, 'about')}` });
  } else if (page === 'portfolio') {
    items.push({ '@type': 'ListItem', position: 2, name: 'Portfolio', item: `${SITE_URL}${localizedPath(lang, 'portfolio')}` });
  } else if (page === 'ai-video') {
    items.push({ '@type': 'ListItem', position: 2, name: 'Video AI', item: `${SITE_URL}${localizedPath(lang, 'ai-video')}` });
  } else if (page === 'project' && projectTitle) {
    items.push({ '@type': 'ListItem', position: 2, name: 'Portfolio', item: `${SITE_URL}${localizedPath(lang, 'portfolio')}` });
    items.push({ '@type': 'ListItem', position: 3, name: projectTitle, item: `${SITE_URL}${localizedPath(lang, 'project', { slug: '' })}` });
  }

  if (items.length < 2) return null;
  return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
}

export function SEO({ page = 'home', path, jsonLd, projectTitle, noIndex = false }) {
  const { lang: urlLang } = useParams();
  const { i18n } = useTranslation();
  const lang = SUPPORTED_LANGS.includes(urlLang) ? urlLang : (i18n.language?.substring(0, 2) || 'it');

  const pageSeo = PAGE_SEO[page]?.[lang] || PAGE_SEO[page]?.en || {};
  const title = pageSeo.title || SITE_NAME;
  const description = pageSeo.description || '';

  const currentPath = path || localizedPath(lang, page);
  const canonical = `${SITE_URL}${currentPath}`;

  // Build hreflang for all languages
  const hreflangs = SUPPORTED_LANGS.map(l => ({
    lang: HREFLANG_MAP[l],
    href: `${SITE_URL}${localizedPath(l, page)}`,
  }));
  hreflangs.push({ lang: 'x-default', href: `${SITE_URL}${localizedPath('it', page)}` });

  const breadcrumbs = buildBreadcrumbs(lang, page, projectTitle);
  const isHome = page === 'home';

  return (
    <Helmet>
      <html lang={HREFLANG_MAP[lang] || 'it-CH'} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="author" content="Double A Studio" />

      {noIndex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />}

      <link rel="canonical" href={canonical} />
      {hreflangs.map(h => <link key={h.lang} rel="alternate" hrefLang={h.lang} href={h.href} />)}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={LOCALE_MAP[lang] || 'it_CH'} />
      {SUPPORTED_LANGS.filter(l => l !== lang).map(l => (
        <meta key={l} property="og:locale:alternate" content={LOCALE_MAP[l]} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {/* Geo */}
      <meta name="geo.region" content="CH" />
      <meta name="geo.placename" content="Switzerland" />

      {/* JSON-LD: Home gets Website + Org + Service */}
      {isHome && <script type="application/ld+json">{JSON.stringify(WEBSITE_JSONLD)}</script>}
      {isHome && <script type="application/ld+json">{JSON.stringify(ORG_JSONLD)}</script>}
      {isHome && <script type="application/ld+json">{JSON.stringify(SERVICE_JSONLD)}</script>}

      {/* Breadcrumbs (all pages except home) */}
      {breadcrumbs && <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>}

      {/* Custom JSON-LD (VideoObject, etc.) */}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}
